import i18n from '@/i18n';
import { colors } from '@/constants/theme';
import type {
  AgreementStatus,
  AgreementType,
  AmountUnit,
  PaymentStatus,
  WalletTransactionKind,
} from '@/lib/database.types';

/**
 * Fallback ονόματος για εμφάνιση όταν το full_name λείπει (π.χ. χρήστης που
 * εγγράφηκε βιαστικά για να υπογράψει πρόσκληση/προσφορά). Ίδια λογική με
 * partyName() στο agreementPdf.ts και resolveDisplayName() στα edge functions.
 */
export function resolveDisplayName(
  profile: { full_name?: string | null; nickname?: string | null; contact_email?: string | null; user_number?: number | null } | null | undefined,
  fallbackEmail?: string | null,
): string {
  return (
    profile?.full_name?.trim() ||
    profile?.nickname?.trim() ||
    profile?.contact_email?.trim() ||
    fallbackEmail?.trim() ||
    (profile?.user_number != null ? i18n.t('common.userNumberFallback', { number: profile.user_number }) : '') ||
    i18n.t('common.someone')
  );
}

/**
 * Μαντεύει το γραμματικό γένος ενός ελληνικού μικρού ονόματος από την
 * κατάληξή του — μόνο για τις σίγουρες περιπτώσεις. Καταλήξεις -ας/-ης/-ος
 * είναι σχεδόν πάντα αρσενικές, -α/-η/-ω σχεδόν πάντα θηλυκές. Ξένα ονόματα
 * ή ασυνήθιστες καταλήξεις επιστρέφουν null (άγνωστο) αντί να μαντεύουν λάθος.
 */
export function inferGender(name: string | null | undefined): 'M' | 'F' | null {
  const first = name?.trim().split(/\s+/)[0]?.toLowerCase();
  if (!first || first.length < 2) return null;
  if (first.endsWith('ας') || first.endsWith('ης') || first.endsWith('ος') || first.endsWith('ές')) return 'M';
  if (first.endsWith('α') || first.endsWith('η') || first.endsWith('ω')) return 'F';
  return null;
}

/**
 * Το άρθρο "Ο"/"Η" πριν από όνομα σε μηνύματα (π.χ. "Ο Γιώργος σου έστειλε…").
 * Πέφτει πίσω σε "Ο/Η" όταν το γένος δεν είναι σίγουρο από την κατάληξη.
 */
export function genderedArticle(name: string | null | undefined): string {
  const g = inferGender(name);
  return g === 'M' ? 'Ο' : g === 'F' ? 'Η' : 'Ο/Η';
}

const STATUS_COLORS: Record<AgreementStatus, string> = {
  draft:                '#94A3B8', // slate — ουδέτερο πρόχειρο
  pending:              '#F59E0B', // amber — αναμονή υπογραφής
  active:               '#3B82F6', // μπλε — ζωντανή
  completed:            '#10B981', // πράσινο — επιτυχία
  cancelled:            '#EF4444', // κόκκινο — ακυρώθηκε
  withdrawn:            '#6B7280', // γκρι — αποσύρθηκε
  disputed:             '#F97316', // πορτοκαλί — επείγον
  terminated:           '#6B7280', // γκρι — τερματίστηκε
  cancel_requested:     '#F59E0B', // amber — αίτημα προσοχής
  cancelled_mutual:     '#8B5CF6', // μωβ — αμοιβαία συμφωνία
  cancelled_unilateral: '#EF4444', // κόκκινο — μονομερής
  pending_completion:   '#8B5CF6', // μωβ — αναμονή επιβεβαίωσης ολοκλήρωσης
  cancelled_unsigned_timeout: '#EF4444', // κόκκινο — ακυρώθηκε λόγω timeout
  overdue:              '#F97316', // πορτοκαλί — πέρασε η προθεσμία, χρειάζεται προσοχή
};

/** Διακριτά χρώματα ανά τύπο συμφωνίας (όχι status — κατηγορία). */
const TYPE_COLORS: Record<AgreementType, string> = {
  one_off: '#A78BFA', // λιλά — μιας φοράς (ορισμένη λήξη)
  ongoing: '#2DD4BF', // teal — μακράς διάρκειας (συνεχής)
  fixed_term: '#2DD4BF', // teal — ίδιο με ongoing (ορισμένης διάρκειας, επαναλαμβανόμενη)
};

export function typeLabel(type: AgreementType): string {
  return i18n.t(`agreements.typeLabel.${type}`);
}

export function typeColor(type: AgreementType): string {
  return TYPE_COLORS[type];
}

export function statusLabel(status: AgreementStatus): string {
  return i18n.t(`agreements.status.${status}`);
}

export function statusColor(status: AgreementStatus): string {
  return STATUS_COLORS[status];
}

/** Ντετερμινιστικό 32-bit hash (djb2) ως μη-αρνητικός ακέραιος. */
function hash32(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return h >>> 0;
}

function chunk4(n: number): string {
  return n.toString(36).toUpperCase().padStart(4, '0').slice(-4);
}

/**
 * Μοναδικός κωδικός ψηφιακής υπογραφής ενός μέρους, ντετερμινιστικός από τη
 * συμφωνία + τον χρήστη + τη χρονοσήμανση. Π.χ. "K3F9-2Q7A". Ίδια στοιχεία → ίδιος
 * κωδικός (επαληθεύσιμο), αλλαγή οποιουδήποτε → διαφορετικός κωδικός.
 */
export function signatureCode(agreementId: string, userId: string, agreedAt: string | null): string {
  const s = `${agreementId}|${userId}|${agreedAt ?? ''}`;
  return `${chunk4(hash32(s))}-${chunk4(hash32('χ' + s))}`;
}

/** Κωδικός ακεραιότητας ολόκληρου του εγγράφου (αλλάζει αν αλλάξει οτιδήποτε). */
export function documentIntegrityCode(parts: (string | number | null | undefined)[]): string {
  const s = parts.map((p) => String(p ?? '')).join('|');
  return `${chunk4(hash32(s))}-${chunk4(hash32('δ' + s))}-${chunk4(hash32('ξ' + s))}`;
}

/** Εισαγωγική πρόταση δέσμευσης: και τα δύο μέρη δεσμεύονται στην τήρηση των όρων. */
export function commitmentIntro(creatorName: string, counterpartyName: string): string {
  const a1 = genderedArticle(creatorName);
  const a2 = genderedArticle(counterpartyName).toLowerCase();
  return `${a1} ${creatorName} και ${a2} ${counterpartyName} δεσμεύονται στην τήρηση των παρακάτω:`;
}

/** Δημόσιο αναγνωριστικό χρήστη, π.χ. 123 → "HEI-00123". */
export function formatUserId(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `HEI-${String(Math.trunc(Number(n))).padStart(5, '0')}`;
}

/** Ασφαλής μετατροπή σε αριθμό — ποτέ NaN (επιστρέφει 0 αν δεν είναι έγκυρος). */
export function safeNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// ── Οροφές ποσοστού Αξιοπιστίας — ΜΟΝΑΔΙΚΗ πηγή αλήθειας, χρησιμοποιούνται
// ΠΑΝΤΟΥ όπου εμφανίζεται το ποσοστό (ReviewSummaryCard, αρχική οθόνη, κ.λπ.)
// ώστε να μη ξαναπροκύψει ασυμφωνία μεταξύ οθονών. ─────────────────────────

/** Η επαλήθευση κινητού με SMS καταργήθηκε (μη αξιόπιστη εκτός Ελλάδας) —
 * μοναδικός δρόμος προς το 100% είναι πλέον η επαλήθευση ταυτότητας. Χωρίς
 * αυτή, το ανώτατο όριο μένει στο baseline 80%. */
export function verificationCapFor(identityVerified?: boolean | null): number {
  return identityVerified ? 100 : 80;
}

/** Βασισμένη στο effective_rating (μετά από ποινές disputes), όχι το raw avg_rating. */
export function ratingCapFor(reviewCount?: number | null, effectiveRating?: number | null): number {
  const count = safeNum(reviewCount);
  const avg = safeNum(effectiveRating);
  if (count < 5) return 100;
  if (avg >= 4.0) return 100;
  if (avg >= 3.5) return 90;
  if (avg >= 3.0) return 75;
  if (avg >= 2.5) return 60;
  return 45;
}

/** Λίγες ολοκληρωμένες συμφωνίες δεν αποδεικνύουν μακροχρόνια αξιοπιστία. */
export function volumeCapFor(completedCount?: number | null): number {
  const n = safeNum(completedCount);
  if (n >= 10) return 100;
  if (n >= 5) return 90;
  if (n >= 3) return 75;
  return 60;
}

/** Συνδυασμένη οροφή — το ελάχιστο των τριών παραπάνω.
 * @param phoneVerified παραμένει στην υπογραφή για συμβατότητα με τα υπάρχοντα
 * call sites, αλλά δεν επηρεάζει πια την οροφή (η επαλήθευση κινητού καταργήθηκε). */
export function reliabilityCap(
  summary: { effective_rating?: number | null; review_count?: number | null; completed_count?: number | null } | null | undefined,
  phoneVerified?: boolean | null,
  identityVerified?: boolean | null,
): number {
  return Math.min(
    verificationCapFor(identityVerified),
    ratingCapFor(summary?.review_count, summary?.effective_rating),
    volumeCapFor(summary?.completed_count),
  );
}

const RELIABILITY_RED = '#EF4444';
const RELIABILITY_ORANGE = '#F97316';
const RELIABILITY_YELLOW = '#FBBF24';
const RELIABILITY_GOLD = '#C9A843';

/** Χρώμα ποσοστού αξιοπιστίας ανά κλίμακα: 0-10 κόκκινο, 11-20 πορτοκάλι,
 * 21-50 κίτρινο, 51-80 πράσινο, 81-100 χρυσό — κοινή πηγή αλήθειας ώστε το
 * χρώμα να ταιριάζει παντού που εμφανίζεται το ποσοστό. */
export function reliabilityColorFor(rate: number | null, successColor: string, mutedColor: string): string {
  if (rate == null) return mutedColor;
  if (rate <= 10) return RELIABILITY_RED;
  if (rate <= 20) return RELIABILITY_ORANGE;
  if (rate <= 50) return RELIABILITY_YELLOW;
  if (rate <= 80) return successColor;
  return RELIABILITY_GOLD;
}

export { RELIABILITY_GOLD };

/** Locale μορφοποίησης αριθμών/ημερομηνιών ανάλογα με τη γλώσσα της εφαρμογής
 * (el → ελληνικό locale, en → en-GB ώστε να διατηρείται η μορφή ΗΗ/ΜΜ/ΕΕΕΕ
 * αντί για την αμερικανική ΜΜ/ΗΗ/ΕΕΕΕ, πιο κοντά σε ό,τι ήδη ξέρει ο χρήστης). */
function currentLocale(): string {
  return i18n.language === 'en' ? 'en-GB' : 'el-GR';
}

/** Μορφοποιεί ποσό σε ευρώ, στη γλώσσα της εφαρμογής. null → παύλα. */
export function formatAmount(amount: number | null, currency = 'EUR'): string {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat(currentLocale(), { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** Μορφοποιεί το «ποσό» συμφωνίας ανάλογα με τη μονάδα (ευρώ ή ποσοστό). */
export function formatAgreementAmount(
  amount: number | null,
  unit: AmountUnit | string = 'eur',
  currency = 'EUR',
): string {
  if (amount == null) return '—';
  if (unit === 'percent') {
    const n = new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 2 }).format(amount);
    return `${n}%`;
  }
  return formatAmount(amount, currency);
}

const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  pending: colors.warning,
  paid: colors.success,
  failed: colors.danger,
  refunded: colors.textMuted,
};

export function paymentStatusLabel(status: PaymentStatus): string {
  return i18n.t(`payment.status.${status}`);
}

export function paymentStatusColor(status: PaymentStatus): string {
  return PAYMENT_STATUS_COLORS[status];
}

/** Ετικέτα είδους κίνησης μελανιού. */
export function walletKindLabel(kind: WalletTransactionKind | string): string {
  return i18n.t(`wallet.kind.${kind}`, kind);
}

/** Μορφοποιεί ποσό κίνησης με πρόσημο (+/−). */
export function formatSignedAmount(amount: number, currency = 'EUR'): string {
  const sign = amount > 0 ? '+' : amount < 0 ? '−' : '';
  return `${sign}${formatAmount(Math.abs(amount), currency)}`;
}

/**
 * Σύντομο όνομα για τον Κύκλο.
 * - Αν υπάρχει nickname: "Άκης Μώρος" (nickname + επώνυμο)
 * - Αλλιώς: "Γ.Κ.Α.Μώρος" (initials όλων πλην επωνύμου + επώνυμο)
 */
export function circleShortName(fullName: string | null, nickname?: string | null): string {
  if (!fullName?.trim()) return '?';
  const parts = fullName.trim().split(/\s+/);
  const last = parts[parts.length - 1];
  if (nickname?.trim()) return `${nickname.trim()} ${last}`;
  if (parts.length === 1) return parts[0].slice(0, 14);
  const inits = parts.slice(0, -1).map((w) => `${w[0]}.`).join('');
  return `${inits}${last}`;
}

/**
 * Όνομα εμφάνισης σε chat/Κύκλο: αν υπάρχει nickname, "nickname επώνυμο"·
 * αλλιώς το πλήρες όνομα όπως είναι (χωρίς συντόμευση). Σε PDF/επίσημα
 * έγγραφα χρησιμοποιείται πάντα το profile.full_name απευθείας, ΠΟΤΕ αυτό.
 */
export function chatDisplayName(fullName: string | null, nickname?: string | null): string {
  if (!fullName?.trim()) return '?';
  if (nickname?.trim()) {
    const parts = fullName.trim().split(/\s+/);
    const last = parts[parts.length - 1];
    return `${nickname.trim()} ${last}`;
  }
  return fullName.trim();
}

/**
 * Σύντομο όνομα για τη γραμμή "Συμμετέχουν οι..." στο chat.
 * - Αν υπάρχει nickname: "Άκης Μώρος" (nickname + επώνυμο)
 * - Αλλιώς: "Αγγ.Μώρος" (πρώτα 3 γράμματα ονόματος + επώνυμο, χωρίς κενό)
 */
export function chatParticipantName(fullName: string | null, nickname?: string | null): string {
  if (!fullName?.trim()) return '?';
  const parts = fullName.trim().split(/\s+/);
  const last = parts[parts.length - 1];
  if (nickname?.trim()) return `${nickname.trim()} ${last}`;
  if (parts.length === 1) return parts[0].slice(0, 14);
  const first = parts[0];
  const abbr = first.length <= 3 ? first : first.slice(0, 3);
  return `${abbr}.${last}`;
}

/**
 * Μασκαρισμένο όνομα για κριτικές (αποκρύπτει επώνυμο).
 * - Αν υπάρχει nickname: "Άκης Μ." (nickname + initial επωνύμου)
 * - Αλλιώς: "Γεώργιος Κ.Α.Μ." (πρώτο όνομα + initials υπολοίπων)
 */
export function reviewMaskName(fullName: string, nickname?: string | null): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName;
  const lastInitial = `${parts[parts.length - 1][0]}.`;
  if (nickname?.trim()) return `${nickname.trim()} ${lastInitial}`;
  const rest = parts.slice(1).map((w) => `${w[0]}.`).join('');
  return `${parts[0]} ${rest}`;
}

/** Title case για ελληνικά ονόματα (κάθε λέξη με κεφαλαίο). */
export function toTitleCase(s: string): string {
  return s
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const GREEK_ONLY_WORD = /^[Α-Ωα-ωϊϋΐΰ]+$/;
const GREEK_ACCENT = /[άέήίόύώΐΰΆΈΉΊΌΎΏ]/;

/** true αν η λέξη είναι αμιγώς ελληνική, 3+ γράμματα, και δεν έχει ΚΑΝΕΝΑ τόνο —
 * σχεδόν σίγουρα ξέχασε ο χρήστης τον τόνο (π.χ. "Γεωργιος" αντί "Γεώργιος"). */
export function isGreekWordMissingAccent(word: string): boolean {
  if (word.length < 3) return false;
  if (!GREEK_ONLY_WORD.test(word)) return false;
  return !GREEK_ACCENT.test(word);
}

/** Εφαρμόζει isGreekWordMissingAccent σε κάθε λέξη ενός πλήρους ονόματος. */
export function fullNameMissingAccents(fullName: string): boolean {
  return fullName
    .trim()
    .split(/\s+/)
    .some(isGreekWordMissingAccent);
}

/** Μορφοποιεί μόνο ημερομηνία, στη γλώσσα της εφαρμογής. */
export function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(currentLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Μορφοποιεί ημερομηνία+ώρα, στη γλώσσα της εφαρμογής. */
export function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(currentLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Ποιο όνομα δείχνουμε για τον αντισυμβαλλόμενο μιας συμφωνίας 1:1.
 *
 * Η σειρά προτεραιότητας είναι σκόπιμη: μόλις ο προσκεκλημένος εγγραφεί,
 * υπερισχύει το όνομα που όρισε Ο ΙΔΙΟΣ στο προφίλ του — όχι αυτό που είχε
 * πληκτρολογήσει ο δημιουργός για λογαριασμό του. Μέχρι τότε δείχνουμε το
 * όνομα που έδωσε ο δημιουργός (σημειωμένο ως εκκρεμής πρόσκληση), κι αν
 * δεν έδωσε όνομα, το email της πρόσκλησης.
 */
export function counterpartyDisplayName(
  agreement: {
    counterparty_id?: string | null;
    counterparty_name?: string | null;
    counterparty_email?: string | null;
  },
  registeredName: string | null | undefined,
  labels: {
    /** Σήμανση ότι η πρόσκληση εκκρεμεί, π.χ. «Γιάννης (πρόσκληση)». */
    invited: (value: string) => string;
    /** Εγγεγραμμένος αλλά χωρίς όνομα στο προφίλ. */
    unknown: string;
    /** Ούτε αντισυμβαλλόμενος, ούτε όνομα, ούτε email. */
    empty: string;
  },
): string {
  if (agreement.counterparty_id) {
    return registeredName?.trim() || labels.unknown;
  }
  const typedName = agreement.counterparty_name?.trim();
  if (typedName) return labels.invited(typedName);

  const email = agreement.counterparty_email?.trim();
  if (email) return labels.invited(email);

  return labels.empty;
}
